import { COUNTERPARTY_RATINGS, CounterpartyRatingProps } from '../../utils/rating';

export const NINEBOX_METRICS = ['%', 'MWm', 'R$'] as const;
export type NineBoxMetricProps = typeof NINEBOX_METRICS[number];

export const NINEBOX_YEARS: number[] = [2026, 2027, 2028, 2029];
export type NineBoxYearProps = number;

export const NINEBOX_RATINGS = [...COUNTERPARTY_RATINGS, 'TOTAL'] as const;
export type NineBoxRatingProps = CounterpartyRatingProps | 'TOTAL';

export type NineBoxCellToneProps =
  | 'green-1'
  | 'green-2'
  | 'green-3'
  | 'yellow-1'
  | 'yellow-2'
  | 'yellow-3'
  | 'red-1'
  | 'red-2'
  | 'red-3'
  | 'positive-1'
  | 'positive-2'
  | 'positive-3'
  | 'positive-4'
  | 'positive-5'
  | 'positive-6'
  | 'positive-7'
  | 'negative-1'
  | 'negative-2'
  | 'negative-3'
  | 'negative-4'
  | 'negative-5'
  | 'negative-6'
  | 'negative-7'
  | 'zero'
  | 'red'
  | 'green'
  | 'yellow'
  | 'orange'
  | 'neutral';

export interface NineBoxCellProps {
  label: string;
  tone: NineBoxCellToneProps;
}

export type NineBoxMetricDataProps = Record<NineBoxRatingProps, Record<number, NineBoxCellProps>>;

const R_MONEY_DATA: NineBoxMetricDataProps = {
  'A+': {
    2026: { label: '-630 Mi', tone: 'red' },
    2027: { label: '-125 Mi', tone: 'red' },
    2028: { label: '-36 Mi', tone: 'orange' },
    2029: { label: '-22 Mi', tone: 'yellow' },
  },
  A: {
    2026: { label: '40 Mi', tone: 'green' },
    2027: { label: '-53 Mi', tone: 'red' },
    2028: { label: '-4 Mi', tone: 'orange' },
    2029: { label: '18 Mi', tone: 'green' },
  },
  B: {
    2026: { label: '13 Mi', tone: 'yellow' },
    2027: { label: '4 Mi', tone: 'yellow' },
    2028: { label: '0 Mi', tone: 'yellow' },
    2029: { label: '11 Mi', tone: 'green' },
  },
  C: {
    2026: { label: '-73 Mi', tone: 'red' },
    2027: { label: '-22 Mi', tone: 'orange' },
    2028: { label: '-6 Mi', tone: 'orange' },
    2029: { label: '-9 Mi', tone: 'orange' },
  },
  'S/D': {
    2026: { label: '-76 Mi', tone: 'red' },
    2027: { label: '-21 Mi', tone: 'orange' },
    2028: { label: '1 Mi', tone: 'yellow' },
    2029: { label: '-2 Mi', tone: 'yellow' },
  },
  TOTAL: {
    2026: { label: '-727 Mi', tone: 'neutral' },
    2027: { label: '-218 Mi', tone: 'neutral' },
    2028: { label: '-45 Mi', tone: 'neutral' },
    2029: { label: '-4 Mi', tone: 'neutral' },
  },
};

const MWM_DATA: NineBoxMetricDataProps = {
  'A+': {
    2026: { label: '451,03', tone: 'red' },
    2027: { label: '304,86', tone: 'red' },
    2028: { label: '225,11', tone: 'orange' },
    2029: { label: '198,40', tone: 'yellow' },
  },
  A: {
    2026: { label: '35,13', tone: 'yellow' },
    2027: { label: '66,86', tone: 'yellow' },
    2028: { label: '6,85', tone: 'green' },
    2029: { label: '24,50', tone: 'green' },
  },
  B: {
    2026: { label: '9,50', tone: 'green' },
    2027: { label: '4,68', tone: 'green' },
    2028: { label: '3,11', tone: 'green' },
    2029: { label: '7,92', tone: 'green' },
  },
  C: {
    2026: { label: '86,30', tone: 'yellow' },
    2027: { label: '71,50', tone: 'yellow' },
    2028: { label: '44,20', tone: 'orange' },
    2029: { label: '31,90', tone: 'orange' },
  },
  'S/D': {
    2026: { label: '96,90', tone: 'red' },
    2027: { label: '52,58', tone: 'yellow' },
    2028: { label: '20,34', tone: 'yellow' },
    2029: { label: '13,11', tone: 'green' },
  },
  TOTAL: {
    2026: { label: '589,59', tone: 'neutral' },
    2027: { label: '491,11', tone: 'neutral' },
    2028: { label: '299,61', tone: 'neutral' },
    2029: { label: '275,83', tone: 'neutral' },
  },
};

const PERCENTAGE_DATA: NineBoxMetricDataProps = {
  'A+': {
    2026: { label: '76,50%', tone: 'red' },
    2027: { label: '62,08%', tone: 'red' },
    2028: { label: '69,19%', tone: 'yellow' },
    2029: { label: '51,33%', tone: 'yellow' },
  },
  A: {
    2026: { label: '5,96%', tone: 'green' },
    2027: { label: '13,61%', tone: 'green' },
    2028: { label: '2,11%', tone: 'green' },
    2029: { label: '19,10%', tone: 'green' },
  },
  B: {
    2026: { label: '1,61%', tone: 'green' },
    2027: { label: '0,95%', tone: 'green' },
    2028: { label: '0,80%', tone: 'green' },
    2029: { label: '6,20%', tone: 'green' },
  },
  C: {
    2026: { label: '14,64%', tone: 'green' },
    2027: { label: '14,56%', tone: 'green' },
    2028: { label: '8,44%', tone: 'green' },
    2029: { label: '12,00%', tone: 'green' },
  },
  'S/D': {
    2026: { label: '16,43%', tone: 'yellow' },
    2027: { label: '10,80%', tone: 'green' },
    2028: { label: '19,46%', tone: 'yellow' },
    2029: { label: '11,37%', tone: 'green' },
  },
  TOTAL: {
    2026: { label: '100,00%', tone: 'neutral' },
    2027: { label: '100,00%', tone: 'neutral' },
    2028: { label: '100,00%', tone: 'neutral' },
    2029: { label: '100,00%', tone: 'neutral' },
  },
};

export const NINEBOX_MOCK_DATA: Record<NineBoxMetricProps, NineBoxMetricDataProps> = {
  '%': PERCENTAGE_DATA,
  MWm: MWM_DATA,
  R$: R_MONEY_DATA,
};
